import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import EditGift from "../Components/Dialog/Gift/EditGift";
import { deleteGift, getGift } from "../store/Gift/gift.action";
import { OPEN_GIFT_DIALOG } from "../store/Gift/gift.type";
import { warning } from "../util/Alert";
import { baseURL } from "../util/config";
import api from "../util/panelApi";
import { Chip, PageHead, Table, Tiles, fmt } from "./Panel/kit";

const cell = { padding: "6px 9px", border: "1px solid #E5E7EB", borderRadius: 6, fontSize: 13 };
const small = { fontSize: 12, color: "#6B7280" };

const thumb = (src) => (
  <div
    style={{
      width: 52,
      height: 52,
      borderRadius: 12,
      background: "#F3F4F6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    }}
  >
    {src ? <img src={src} alt="" style={{ width: 46, height: 46, objectFit: "contain" }} draggable="false" /> : "—"}
  </div>
);

/// Hediyeler: tek tabloda ad, kategori, fiyat, animasyon ve platform.
const Gift = (props) => {
  const { gift } = useSelector((state) => state.gift);
  const dispatch = useDispatch();
  const history = useHistory();

  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("coin");
  const [bigCoin, setBigCoin] = useState(0);

  const category = JSON.parse(sessionStorage.getItem("category"));
  const giftClick = sessionStorage.getItem("giftClick");

  useEffect(() => {
    dispatch(getGift(giftClick == null ? category?._id : "ALL"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, giftClick]);

  useEffect(() => {
    api.config().then((r) => {
      if (r?.status) setBigCoin(Number(r.config.bigGiftBannerCoin) || 0);
    });
  }, []);

  // Sunucu "ALL" istendiginde kategorileri, tek kategori istendiginde hediyeleri doner.
  // Ikisini de tek duz listeye cevirip tabloya veriyoruz.
  const rows = useMemo(() => {
    const list = Array.isArray(gift) ? gift : [];
    const isCategoryList = list.length > 0 && Array.isArray(list[0]?.gift);
    if (!isCategoryList) {
      return list.map((g) => ({ ...g, categoryName: category?.name || "—" }));
    }
    const out = [];
    for (const c of list) {
      for (const g of c.gift || []) out.push({ ...g, categoryName: c.name || "—" });
    }
    return out;
  }, [gift, category]);

  const categories = useMemo(() => [...new Set(rows.map((r) => r.categoryName))].sort(), [rows]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (cat && r.categoryName !== cat) return false;
      if (!needle) return true;
      return (
        String(r.name || "").toLowerCase().includes(needle) ||
        String(r.coin || "").includes(needle) ||
        String(r.categoryName || "").toLowerCase().includes(needle)
      );
    });
    out = [...out].sort((a, b) => {
      if (sort === "coinAsc") return (a.coin || 0) - (b.coin || 0);
      if (sort === "name") return String(a.name || "").localeCompare(String(b.name || ""), "tr");
      if (sort === "new") return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      return (b.coin || 0) - (a.coin || 0);
    });
    return out;
  }, [rows, q, cat, sort]);

  const handleEdit = useCallback((data) => dispatch({ type: OPEN_GIFT_DIALOG, payload: data }), [dispatch]);

  const handleDelete = (row) => {
    warning()
      .then((isDeleted) => {
        if (isDeleted) props.deleteGift(row._id);
      })
      .catch((err) => console.log(err));
  };

  const priciest = rows.reduce((a, r) => Math.max(a, Number(r.coin) || 0), 0);
  const withSvga = rows.filter((r) => r.svga).length;
  const bigOnes = bigCoin > 0 ? rows.filter((r) => (Number(r.coin) || 0) >= bigCoin).length : 0;

  return (
    <>
      <PageHead
        title={giftClick === null ? `${category?.name || ""} hediyeleri` : "Tüm hediyeler"}
        sub="Hediye gönderilince coin harcanır; yayıncı payı Ekonomi sayfasındaki orana göre hesaplanır."
        right={
          <button
            className="btn btn-danger"
            onClick={() =>
              history.push(giftClick == null ? "/admin/giftCategory/dialog" : "/admin/gift/dialog")
            }
          >
            Yeni hediye
          </button>
        }
      />

      <Tiles
        items={[
          { label: "Hediye sayısı", value: fmt(rows.length) },
          { label: "Kategori", value: categories.length },
          { label: "Animasyonlu (SVGA)", value: fmt(withSvga) },
          {
            label: bigCoin > 0 ? `Büyük hediye (${fmt(bigCoin)}+)` : "Büyük hediye",
            value: bigCoin > 0 ? fmt(bigOnes) : "—",
          },
          { label: "En pahalı", value: fmt(priciest) },
        ]}
      />

      <div className="d-flex" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input
          style={{ ...cell, width: 240 }}
          placeholder="Ara: ad, fiyat, kategori"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select style={{ ...cell, width: 190 }} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">Tüm kategoriler</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select style={{ ...cell, width: 190 }} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="coin">Pahalıdan ucuza</option>
          <option value="coinAsc">Ucuzdan pahalıya</option>
          <option value="name">Ada göre</option>
          <option value="new">En yeni</option>
        </select>
        <span style={{ ...small, alignSelf: "center" }}>{shown.length} hediye gösteriliyor</span>
      </div>

      <Table
        head={["Görsel", "Ad", "Kategori", "Fiyat", "Animasyon", ""]}
        rows={shown.map((r) => {
          const coin = Number(r.coin) || 0;
          const big = bigCoin > 0 && coin >= bigCoin;
          return (
            <tr key={r._id}>
              <td>{thumb(r.image ? baseURL + r.image : "")}</td>
              <td>
                <b>{r.name || "—"}</b>
                {big && (
                  <div style={{ marginTop: 3 }}>
                    <Chip kind="wait">Büyük hediye · şerit çıkar</Chip>
                  </div>
                )}
              </td>
              <td style={small}>{r.categoryName}</td>
              <td>
                <b style={{ fontSize: 15 }}>{fmt(coin)}</b>
                <div style={small}>coin</div>
              </td>
              <td>
                {r.svga ? <Chip>SVGA</Chip> : r.type === 1 ? <Chip kind="info">GIF</Chip> : <span style={small}>Sabit resim</span>}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="btn btn-sm btn-info" onClick={() => handleEdit(r)} style={{ marginRight: 6 }}>
                  <i className="fas fa-edit text-white"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r)}>
                  <i className="fas fa-trash text-white"></i>
                </button>
              </td>
            </tr>
          );
        })}
        empty="Gösterilecek hediye yok."
      />

      <EditGift />
    </>
  );
};

export default connect(null, { getGift, deleteGift })(Gift);
